import * as React from 'react';
import { useEffect, useRef, useState, useCallback } from 'react';

/** Material UI Icons */
import BorderColorIcon from '@mui/icons-material/BorderColor';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import ClearIcon from '@mui/icons-material/Clear';

/** Material UI Packages. */
import { Autocomplete, createFilterOptions, debounce, TextField } from '@mui/material';
import { GridActionsCellItem, GridRowModes } from '@mui/x-data-grid-premium';

/** Redux Packages. */
import { useDispatch } from 'react-redux';

/** Internal Components. */
import { readSocketData } from 'shared/lib/fetchSocket';

/**
 * Data Entry tab number -> the numbering the location autocomplete speaks.
 *
 * These are two different vocabularies, and they have to stay that way. The backend's
 * `il_location_route` reads 0 as ocean ports and anything else as air, and the same
 * number picks the option label (0 shows Code | Name | Country, 1 shows the name
 * alone). Teaching the backend the Data Entry numbering instead would change the wire
 * format, and there is no safe deploy order for that: while only one side is updated,
 * the ocean and rail tabs silently receive airports, whose country is always blank in
 * il_location, and that blank is what gets saved into il_route.
 *
 * Rail runs between ocean ports as well - a rail leg starts at the POD and ends at the
 * final destination - so RAIL ROUTE maps to 0 like OCEAN ROUTE does, which is also
 * what gives it the Code / Name / Country option labels.
 *
 * Only these three tabs render this grid, so any other value is a bug rather than a
 * case to support; it falls back to air, the backend's own default.
 */
const LOCATION_TAB_BY_DATA_ENTRY_TAB = { 1: 0, 2: 1, 3: 0 };

const locationTabOf = (tab) => LOCATION_TAB_BY_DATA_ENTRY_TAB[tab] ?? 1;

/** DataGrid route definition. */
export const routeDef = (tab, rowModesModel, columnDefFeature) => {
    const { handleSaveClick, handleCancelClick, handleEditClick, handleDeleteClick, handleAutocompleteChange } =
        columnDefFeature;

    const allColumns = [
        {
            field: 'id',
            headerName: 'ID',
            headerAlign: 'center',
            headerClassName: 'normal-column-style',
            align: 'center',
            editable: false,
        },
        {
            field: 'pol_code',
            headerName: 'Code',
            headerClassName: 'normal-column-style',
            headerAlign: 'center',
            align: 'center',
            type: 'string',
            flex: 0.3,
        },
        {
            field: 'pol_name',
            headerName: 'Name',
            headerClassName: 'normal-column-style',
            headerAlign: 'center',
            align: 'center',
            editable: true,
            flex: 1.1,
            renderEditCell: (params) => {
                return (
                    <PolPodAutocomplete
                        params={params}
                        onChange={handleAutocompleteChange}
                        tabIndex={1}
                        locationTab={locationTabOf(tab)}
                    />
                );
            },
        },
        {
            field: 'pol_country',
            headerName: 'Country',
            headerClassName: 'normal-separator-column-style',
            headerAlign: 'center',
            align: 'center',
            editable: false,
            flex: 0.7,
        },
        {
            field: 'pod_code',
            headerName: 'Code',
            headerClassName: 'normal-column-style',
            headerAlign: 'center',
            align: 'center',
            editable: false,
            flex: 0.3,
        },
        {
            field: 'pod_name',
            headerName: 'Name',
            headerClassName: 'normal-column-style',
            headerAlign: 'center',
            align: 'center',
            editable: true,
            flex: 1.1,
            renderEditCell: (params) => {
                return (
                    <PolPodAutocomplete
                        params={params}
                        onChange={handleAutocompleteChange}
                        tabIndex={2}
                        locationTab={locationTabOf(tab)}
                    />
                );
            },
        },
        {
            field: 'pod_country',
            headerName: 'Country',
            headerClassName: 'normal-separator-column-style',
            headerAlign: 'center',
            align: 'center',
            editable: false,
            flex: 0.7,
        },
        {
            field: 'leadtime',
            headerName: 'Days',
            headerClassName: 'normal-separator-column-style',
            headerAlign: 'center',
            align: 'center',
            editable: true,
            type: 'number',
            flex: 0.9,
        },
        {
            field: 'actions',
            type: 'actions',
            headerName: '',
            headerClassName: 'normal-column-style',
            align: 'center',
            headerAlign: 'center',
            flex: 0.25,
            getActions: (params) => {
                const { api, id } = params;
                const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

                if (isInEditMode) {
                    return [
                        <GridActionsCellItem
                            sx={{
                                bgcolor: '#003a52',
                                color: 'white',
                                '&:hover': { color: '#003a52' },
                                p: '5px',
                            }}
                            icon={<SaveIcon sx={{ fontSize: '13px' }} />}
                            label="edit"
                            onClick={handleSaveClick(id)}
                            color="secondary"
                        />,
                        <GridActionsCellItem
                            sx={{
                                bgcolor: '#003a52',
                                color: 'white',
                                '&:hover': { color: '#003a52' },
                                p: '5px',
                            }}
                            icon={<ClearIcon sx={{ fontSize: '13px' }} />}
                            label="edit"
                            onClick={handleCancelClick(id)}
                            color="secondary"
                        />,
                    ];
                } else {
                    return [
                        <GridActionsCellItem
                            sx={{
                                bgcolor: '#003a52',
                                color: 'white',
                                '&:hover': { color: '#003a52' },
                                p: '5px',
                            }}
                            icon={<BorderColorIcon sx={{ fontSize: '13px' }} />}
                            label="edit"
                            onClick={handleEditClick(id, api)}
                            color="secondary"
                        />,
                        <GridActionsCellItem
                            sx={{
                                bgcolor: '#003a52',
                                color: 'white',
                                '&:hover': { color: '#003a52' },
                                p: '5px',
                            }}
                            icon={<DeleteIcon sx={{ fontSize: '13px' }} />}
                            label="edit"
                            onClick={handleDeleteClick(id)}
                            color="secondary"
                        />,
                    ];
                }
            },
        },
    ];

    return allColumns;
};

/** Data Grid Coulumn Header Grouping */
export const oceanColGroupingModel = [
    {
        groupId: 'Internal',
        headerAlign: 'center',
        headerClassName: 'grouped-column-style',
        description: '',
        children: [{ field: 'id' }],
    },
    {
        groupId: 'POL',
        headerAlign: 'center',
        headerClassName: 'grouped-column-style',
        border: 'solid 1px #ffffff',
        children: [{ field: 'pol_code' }, { field: 'pol_name' }, { field: 'pol_country' }],
    },
    {
        groupId: 'POD',
        headerAlign: 'center',
        headerClassName: 'grouped-column-style',
        description: '',
        children: [{ field: 'pod_code' }, { field: 'pod_name' }, { field: 'pod_country' }],
    },
    {
        groupId: 'Lead Time',
        headerAlign: 'center',
        headerClassName: 'grouped-column-style',
        description: '',
        children: [{ field: 'leadtime' }],
    },
    {
        groupId: 'Actions',
        headerAlign: 'center',
        headerClassName: 'grouped-column-style',
        description: '',
        children: [{ field: 'actions' }],
    },
];

/**
 * Rail routes reuse the same il_route columns as ocean and air, but the pair means
 * POD -> F.DEST: where the container is discharged, and where the rail leg ends.
 */
export const railColGroupingModel = [
    {
        groupId: 'Internal',
        headerAlign: 'center',
        headerClassName: 'grouped-column-style',
        description: '',
        children: [{ field: 'id' }],
    },
    {
        groupId: 'POD',
        headerAlign: 'center',
        headerClassName: 'grouped-column-style',
        border: 'solid 1px #ffffff',
        children: [{ field: 'pol_code' }, { field: 'pol_name' }, { field: 'pol_country' }],
    },
    {
        groupId: 'F.DEST',
        headerAlign: 'center',
        headerClassName: 'grouped-column-style',
        description: '',
        children: [{ field: 'pod_code' }, { field: 'pod_name' }, { field: 'pod_country' }],
    },
    {
        groupId: 'Lead Time',
        headerAlign: 'center',
        headerClassName: 'grouped-column-style',
        description: '',
        children: [{ field: 'leadtime' }],
    },
    {
        groupId: 'Actions',
        headerAlign: 'center',
        headerClassName: 'grouped-column-style',
        description: '',
        children: [{ field: 'actions' }],
    },
];

export const airColGroupingModel = [
    {
        groupId: 'Internal',
        headerAlign: 'center',
        headerClassName: 'grouped-column-style',
        description: '',
        children: [{ field: 'id' }],
    },
    {
        groupId: 'AOL',
        headerAlign: 'center',
        headerClassName: 'grouped-column-style',
        border: 'solid 1px #ffffff',
        children: [{ field: 'pol_code' }, { field: 'pol_name' }, { field: 'pol_country' }],
    },
    {
        groupId: 'AOD',
        headerAlign: 'center',
        headerClassName: 'grouped-column-style',
        description: '',
        children: [{ field: 'pod_code' }, { field: 'pod_name' }, { field: 'pod_country' }],
    },
    {
        groupId: 'Lead Time',
        headerAlign: 'center',
        headerClassName: 'grouped-column-style',
        description: '',
        children: [{ field: 'leadtime' }],
    },
    {
        groupId: 'Actions',
        headerAlign: 'center',
        headerClassName: 'grouped-column-style',
        description: '',
        children: [{ field: 'actions' }],
    },
];

/** Autocomplete option render limit */
const OPTIONS_LIMIT = 100;

/**
 * Custom autocomplete for selecting POL/POD.
 * `locationTab` is the location-search numbering (0 ocean, 1 air), NOT the Data Entry
 * tab index - see LOCATION_TAB_BY_DATA_ENTRY_TAB.
 */
const PolPodAutocomplete = ({ locationTab, params, onChange }) => {
    /** Dispatch a function to update an edited trade partner. */
    const dispatch = useDispatch();

    const [locationList, setLocationList] = useState([]);
    /** State to assign in autocomplete's value. */
    const [editValue, setEditValue] = useState(params.formattedValue);
    /** Reference to make focus when creating or editing routes. */
    const inputRef = useRef(null);

    /** Variable to indicate the maximum number of autocomplete options to display. */
    const filterOptions = createFilterOptions({ limit: OPTIONS_LIMIT });

    /** Emit a socket event to search for suitable location options. */
    const handleDebounceChange = useCallback(
        debounce((value) => {
            readSocketData(dispatch, locationTab, 'row', 'il_location_route', '', value, (response) => {
                setLocationList(response);
            });
        }, 300),
        []
    );

    /** Handler to search the ocean carriers. */
    const handleTypingChange = (event) => {
        const value = event.target.value;
        handleDebounceChange(value);
    };

    /** Ocean and rail options show Code | Name | Country; air shows the name alone. */
    const convertOptionLabel = (option) => {
        if (!option) return '';
        if (locationTab === 0) {
            return `${option.code} | ${option.name} | ${option.country}`;
        }
        return `${option.name}`;
    };

    const handleChange = (event, newValue) => {
        onChange(params, newValue);
        setEditValue(newValue?.name || '');
    };

    /** Automatically focus the input when hasFocus becomes true. */
    useEffect(() => {
        if (params.hasFocus && inputRef.current) {
            inputRef.current.focus();
        }
    }, [params.hasFocus]);

    return (
        <Autocomplete
            size="small"
            value={editValue || null}
            inputValue={editValue}
            options={locationList}
            filterOptions={filterOptions}
            fullWidth
            disableClearable
            getOptionKey={(option) => option.id || ''}
            getOptionLabel={convertOptionLabel}
            isOptionEqualToValue={(option) => option.id === params.value}
            onChange={handleChange}
            onInputChange={(event, newValue, reason) => {
                if (reason === 'input') {
                    setEditValue(newValue);
                }
            }}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label=""
                    inputRef={inputRef}
                    onChange={handleTypingChange}
                    onKeyDown={(event) => {
                        if (event.code === 'Tab') {
                            event.stopPropagation();
                        }
                    }}
                />
            )}
        />
    );
};
